/**
 * Drill CSV Export Utilities — FT-468
 *
 * Provides reusable CSV generation helpers for drill data.
 * Designed as a generic foundation (headers + rows → CSV string) that later
 * epics can roll out to other registers without copy-paste.
 *
 * Excel-safe: UTF-8, RFC-4180 quoting (quote fields that contain commas,
 * double-quotes, or newlines; internal double-quotes doubled).
 */


// ---------------------------------------------------------------------------
// Generic CSV utilities (reusable across registers)
// ---------------------------------------------------------------------------

/**
 * Escape a single CSV field per RFC-4180.
 * - Converts null/undefined to empty string.
 * - Wraps in double-quotes when the value contains: comma, double-quote, newline.
 * - Doubles any internal double-quotes.
 *
 * @param {*} value - Raw cell value
 * @returns {string} Safe CSV field
 */
export function escapeCSVField(value) {
  if (value == null) return '';
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
    return '"' + str.replace(/"/g, '""') + '"';
  }
  return str;
}

/**
 * Build a complete CSV string from headers and row data.
 *
 * @param {string[]} headers - Column header labels (in order)
 * @param {Array<Array<*>>} rows - 2-D array; each inner array must match headers length
 * @returns {string} Full CSV content (headers + rows, CRLF line endings)
 */
export function buildCSV(headers, rows) {
  const headerLine = headers.map(escapeCSVField).join(',');
  const dataLines = rows.map(row => row.map(escapeCSVField).join(','));
  return [headerLine, ...dataLines].join('\r\n');
}

/**
 * Trigger a browser download of CSV content as a .csv file.
 * UTF-8 BOM prefix ensures Excel (Windows) opens without encoding dialog.
 *
 * @param {string} csvContent - Full CSV string
 * @param {string} filename - File name including .csv extension
 */
export function downloadCSV(csvContent, filename) {
  const BOM = '﻿'; // UTF-8 BOM for Excel
  const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Release the object URL after a short delay
  setTimeout(() => URL.revokeObjectURL(url), 100);
}

// ---------------------------------------------------------------------------
// Drill-specific mappers
// ---------------------------------------------------------------------------

/**
 * Column headers for the drills / history CSV report.
 * Must match the PDF table columns: Date | Drill | Attendees | Duration | Outcome | Notes
 */
export const DRILL_HISTORY_CSV_HEADERS = [
  'Date',
  'Drill',
  'Attendees',
  'Duration (mins)',
  'Outcome',
  'Notes'
];

/**
 * Format a date value to DD/MM/YYYY for CSV (matches PDF date display).
 * Returns empty string for null / invalid dates.
 *
 * @param {string|Date|null} date
 * @returns {string}
 */
function formatDrillDate(date) {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB');
}

/**
 * Format drill outcome to a human-readable label.
 *
 * @param {string|null} outcome
 * @returns {string}
 */
function formatOutcome(outcome) {
  switch (outcome) {
    case 'Pass':       return 'Pass';
    case 'pass':       return 'Pass';
    case 'success':    return 'Pass';
    case 'Fail':       return 'Fail';
    case 'fail':       return 'Fail';
    case 'failed':     return 'Fail';
    case 'partial':    return 'Needs Improvement';
    case 'Partial':    return 'Needs Improvement';
    default:           return outcome || '';
  }
}

/**
 * Map a single drill record to a CSV row aligned with DRILL_HISTORY_CSV_HEADERS.
 * FT-387 guards: type and crew are optional in production data.
 *
 * FIX 3 (FT-468): Attendees now reads from `participants` (the field used by
 * completion records) with a fallback to legacy `crew`. `crew` is always absent
 * on completion records — that was why every Attendees cell was blank.
 *
 * @param {Object} drill - Drill record
 * @returns {Array<*>} Row cells (unescaped; escapeCSVField handles quoting)
 */
export function drillToCSVRow(drill) {
  const date = formatDrillDate(drill.date);
  const drillType = (drill.type || '').replace(/_/g, ' ');

  // FIX B (FT-468 Attempt 3): prefer participants over crew.
  // participants may be an array of OBJECTS e.g. [{name:'Billy Sparks'}, ...].
  // Calling p.join('; ') directly produces '[object Object]'. Extract the
  // display name from each element, filter blanks, then join.
  const p = drill.participants;
  let attendees;
  if (p != null) {
    if (Array.isArray(p)) {
      const names = p
        .map(item =>
          typeof item === 'string'
            ? item
            : (item?.name || item?.crewName || item?.fullName || item?.displayName || '')
        )
        .filter(Boolean);
      attendees = names.length > 0 ? names.join('; ') : '';
    } else if (typeof p === 'number') {
      attendees = `${p} crew`;
    } else {
      attendees = String(p);
    }
  } else {
    // Legacy crew fallback (FT-387: crew may be absent); same object-safe extraction
    const c = drill.crew;
    if (Array.isArray(c)) {
      const names = c
        .map(item =>
          typeof item === 'string'
            ? item
            : (item?.name || item?.crewName || item?.fullName || item?.displayName || '')
        )
        .filter(Boolean);
      attendees = names.length > 0 ? names.join('; ') : '';
    } else {
      attendees = c || '';
    }
  }

  const duration = drill.duration != null ? drill.duration : '';
  const outcome = formatOutcome(drill.outcome);
  const notes = drill.notes || '';

  return [date, drillType, attendees, duration, outcome, notes];
}

/**
 * Generate a CSV string for an array of drill records (drills / history report).
 * Safe for empty or undefined arrays.
 *
 * @param {Array} drills - Drill records (may be empty)
 * @returns {string} CSV content string
 */
export function generateDrillHistoryCSV(drills) {
  const safeDrills = Array.isArray(drills) ? drills : [];
  const rows = safeDrills.map(drillToCSVRow);
  return buildCSV(DRILL_HISTORY_CSV_HEADERS, rows);
}

/**
 * Download the drill history CSV — main entry point for the UI.
 *
 * @param {Array} drills - Filtered drill records to export
 * @param {Object} options - { vesselName, dateRangeText }
 */
export function exportDrillHistoryCSV(drills, options = {}) {
  const csvContent = generateDrillHistoryCSV(drills);
  const vesselSlug = (options.vesselName || 'vessel')
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
  const dateStr = new Date().toISOString().split('T')[0];
  const filename = `safety_drills_${vesselSlug}_${dateStr}.csv`;

  downloadCSV(csvContent, filename);
}
